import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { admissionService } from '../../services/admissionService';
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  Upload, 
  FileText, 
  Image, 
  X, 
  Calendar,
  User,
  School,
  Phone,
  Mail,
  MapPin,
  Heart,
  Shield,
  AlertCircle,
  CheckCircle,
  Clock,
  Download,
  Eye,
  Trash2,
  Plus,
  Minus
} from 'lucide-react';

const AdmissionForm = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [uploadedFiles, setUploadedFiles] = React.useState([]);
  const [applicationNumber, setApplicationNumber] = React.useState('');
  const [formData, setFormData] = React.useState({
    // Step 1: Personal Information
    firstName: '',
    lastName: '',
    middleName: '',
    dateOfBirth: '',
    gender: '',
    nationality: 'Nigerian',
    stateOfOrigin: '',
    localGovernment: '',
    religion: '',
    bloodGroup: '',
    placeOfBirth: '',
    
    // Step 2: Academic Information
    previousSchool: '',
    lastClassCompleted: '',
    desiredClass: '',
    academicYear: '2024/2025',
    subjects: [],
    previousGrades: '',
    reasonForTransfer: '',
    
    // Step 3: Contact Information
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    
    // Step 4: Emergency Contact
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: '',
    emergencyContactAddress: '',
    
    // Step 5: Parent/Guardian Information
    parentFirstName: '',
    parentLastName: '',
    parentPhone: '',
    parentEmail: '',
    parentOccupation: '',
    parentAddress: '',
    parentWorkplace: '',
    parentWorkPhone: '',
    
    // Step 6: Medical Information
    medicalConditions: '',
    allergies: '',
    medications: '',
    specialNeeds: '',
    medicalInsurance: '',
    insuranceProvider: '',
    insuranceNumber: '',
    
    // Step 7: Additional Information
    transportation: '',
    previousDisciplinary: false,
    disciplinaryDetails: '',
    extracurricularActivities: [],
    interests: '',
    goals: '',
    
    // Step 8: Documents & Review
    agreeToTerms: false,
    agreeToPrivacy: false,
    agreeToMedical: false,
    agreeToDisciplinary: false,
  });

  const steps = [
    { id: 'personal', title: 'Personal Information', icon: User },
    { id: 'academic', title: 'Academic Information', icon: School },
    { id: 'contact', title: 'Contact Information', icon: Phone },
    { id: 'emergency', title: 'Emergency Contact', icon: AlertCircle },
    { id: 'parent', title: 'Parent/Guardian', icon: Heart },
    { id: 'medical', title: 'Medical Information', icon: Shield },
    { id: 'additional', title: 'Additional Information', icon: FileText },
    { id: 'documents', title: 'Documents & Review', icon: Upload },
  ];

  const requiredDocuments = [
    { name: 'Birth Certificate', type: 'birth_certificate', required: true },
    { name: 'Previous School Report Card', type: 'report_card', required: true },
    { name: 'Passport Photograph', type: 'passport_photo', required: true },
    { name: 'Medical Certificate', type: 'medical_certificate', required: true },
    { name: 'Immunization Record', type: 'immunization', required: false },
    { name: 'Transfer Certificate', type: 'transfer_certificate', required: false },
    { name: 'Character Certificate', type: 'character_certificate', required: false },
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Auto-save draft
    saveDraft();
  };

  const saveDraft = async () => {
    try {
      await admissionService.saveDraft(formData);
    } catch (error) {
      console.error('Failed to save draft:', error);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    try {
      setLoading(true);
      const uploadPromises = files.map(file => {
        const formData = new FormData();
        formData.append('file', file);
        return admissionService.uploadDocuments(formData);
      });
      
      const responses = await Promise.all(uploadPromises);
      const newFiles = responses.map((res, index) => ({
        id: res.data.id,
        name: files[index].name,
        type: files[index].type,
        size: files[index].size,
        url: res.data.url,
        documentType: 'other'
      }));
      
      setUploadedFiles(prev => [...prev, ...newFiles]);
    } catch (error) {
      console.error('Failed to upload files:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const handleSetDocumentType = (fileId, documentType) => {
    setUploadedFiles(prev => 
      prev.map(file => 
        file.id === fileId ? { ...file, documentType } : file
      )
    );
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      
      // Generate application number if not exists
      if (!applicationNumber) {
        const response = await admissionService.generateApplicationNumber();
        setApplicationNumber(response.data.applicationNumber);
      }
      
      // Submit application
      const submissionData = {
        ...formData,
        applicationNumber,
        documents: uploadedFiles
      };
      
      const response = await admissionService.submitApplication(submissionData);
      
      // Send confirmation email
      await admissionService.sendConfirmationEmail(response.data.id);
      
      // Navigate to success page
      navigate(`/admission/success?applicationNumber=${applicationNumber}`);
    } catch (error) {
      console.error('Failed to submit application:', error);
    } finally {
      setSaving(false);
    }
  };

  const isStepValid = (step) => {
    switch (step) {
      case 0: // Personal
        return formData.firstName && formData.lastName && formData.dateOfBirth && formData.gender;
      case 1: // Academic
        return formData.previousSchool && formData.lastClassCompleted && formData.desiredClass;
      case 2: // Contact
        return formData.email && formData.phone && formData.address;
      case 3: // Emergency
        return formData.emergencyContactName && formData.emergencyContactPhone;
      case 4: // Parent
        return formData.parentFirstName && formData.parentLastName && formData.parentPhone;
      case 5: // Medical
        return true; // Optional step
      case 6: // Additional
        return true; // Optional step
      case 7: // Documents
        return formData.agreeToTerms && formData.agreeToPrivacy;
      default:
        return false;
    }
  };

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return <Image className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Personal Information
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  placeholder="Enter first name"
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  placeholder="Enter last name"
                />
              </div>
              <div>
                <Label htmlFor="middleName">Middle Name</Label>
                <Input
                  id="middleName"
                  value={formData.middleName}
                  onChange={(e) => handleInputChange('middleName', e.target.value)}
                  placeholder="Enter middle name"
                />
              </div>
              <div>
                <Label htmlFor="dateOfBirth">Date of Birth *</Label>
                <Input
                  id="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="gender">Gender *</Label>
                <Select value={formData.gender} onValueChange={(value) => handleInputChange('gender', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="nationality">Nationality</Label>
                <Input
                  id="nationality"
                  value={formData.nationality}
                  onChange={(e) => handleInputChange('nationality', e.target.value)}
                  placeholder="Enter nationality"
                />
              </div>
            </div>
          </div>
        );

      case 1: // Academic Information
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="previousSchool">Previous School *</Label>
                <Input
                  id="previousSchool"
                  value={formData.previousSchool}
                  onChange={(e) => handleInputChange('previousSchool', e.target.value)}
                  placeholder="Enter previous school name"
                />
              </div>
              <div>
                <Label htmlFor="lastClassCompleted">Last Class Completed *</Label>
                <Select value={formData.lastClassCompleted} onValueChange={(value) => handleInputChange('lastClassCompleted', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select class" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="JSS1">JSS1</SelectItem>
                    <SelectItem value="JSS2">JSS2</SelectItem>
                    <SelectItem value="JSS3">JSS3</SelectItem>
                    <SelectItem value="SSS1">SSS1</SelectItem>
                    <SelectItem value="SSS2">SSS2</SelectItem>
                    <SelectItem value="SSS3">SSS3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="desiredClass">Desired Class *</Label>
                <Select value={formData.desiredClass} onValueChange={(value) => handleInputChange('desiredClass', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select desired class" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="JSS1">JSS1</SelectItem>
                    <SelectItem value="JSS2">JSS2</SelectItem>
                    <SelectItem value="JSS3">JSS3</SelectItem>
                    <SelectItem value="SSS1">SSS1</SelectItem>
                    <SelectItem value="SSS2">SSS2</SelectItem>
                    <SelectItem value="SSS3">SSS3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="academicYear">Academic Year</Label>
                <Input
                  id="academicYear"
                  value={formData.academicYear}
                  onChange={(e) => handleInputChange('academicYear', e.target.value)}
                  placeholder="2024/2025"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="reasonForTransfer">Reason for Transfer</Label>
              <Textarea
                id="reasonForTransfer"
                value={formData.reasonForTransfer}
                onChange={(e) => handleInputChange('reasonForTransfer', e.target.value)}
                placeholder="Explain reason for transferring to our school"
                rows={3}
              />
            </div>
          </div>
        );

      case 2: // Contact Information
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Enter email address"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="Enter phone number"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="address">Address *</Label>
              <Textarea
                id="address"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="Enter full address"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder="Enter city"
                />
              </div>
              <div>
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  placeholder="Enter state"
                />
              </div>
              <div>
                <Label htmlFor="postalCode">Postal Code</Label>
                <Input
                  id="postalCode"
                  value={formData.postalCode}
                  onChange={(e) => handleInputChange('postalCode', e.target.value)}
                  placeholder="Enter postal code"
                />
              </div>
            </div>
          </div>
        );

      case 3: // Emergency Contact
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="emergencyContactName">Emergency Contact Name *</Label>
                <Input
                  id="emergencyContactName"
                  value={formData.emergencyContactName}
                  onChange={(e) => handleInputChange('emergencyContactName', e.target.value)}
                  placeholder="Enter emergency contact name"
                />
              </div>
              <div>
                <Label htmlFor="emergencyContactPhone">Emergency Contact Phone *</Label>
                <Input
                  id="emergencyContactPhone"
                  value={formData.emergencyContactPhone}
                  onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)}
                  placeholder="Enter emergency contact phone"
                />
              </div>
              <div>
                <Label htmlFor="emergencyContactRelationship">Relationship</Label>
                <Select value={formData.emergencyContactRelationship} onValueChange={(value) => handleInputChange('emergencyContactRelationship', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select relationship" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parent">Parent</SelectItem>
                    <SelectItem value="guardian">Guardian</SelectItem>
                    <SelectItem value="sibling">Sibling</SelectItem>
                    <SelectItem value="relative">Relative</SelectItem>
                    <SelectItem value="friend">Family Friend</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="emergencyContactAddress">Emergency Contact Address</Label>
              <Textarea
                id="emergencyContactAddress"
                value={formData.emergencyContactAddress}
                onChange={(e) => handleInputChange('emergencyContactAddress', e.target.value)}
                placeholder="Enter emergency contact address"
                rows={3}
              />
            </div>
          </div>
        );

      case 4: // Parent/Guardian Information
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="parentFirstName">Parent/Guardian First Name *</Label>
                <Input
                  id="parentFirstName"
                  value={formData.parentFirstName}
                  onChange={(e) => handleInputChange('parentFirstName', e.target.value)}
                  placeholder="Enter parent first name"
                />
              </div>
              <div>
                <Label htmlFor="parentLastName">Parent/Guardian Last Name *</Label>
                <Input
                  id="parentLastName"
                  value={formData.parentLastName}
                  onChange={(e) => handleInputChange('parentLastName', e.target.value)}
                  placeholder="Enter parent last name"
                />
              </div>
              <div>
                <Label htmlFor="parentPhone">Parent/Guardian Phone *</Label>
                <Input
                  id="parentPhone"
                  value={formData.parentPhone}
                  onChange={(e) => handleInputChange('parentPhone', e.target.value)}
                  placeholder="Enter parent phone number"
                />
              </div>
              <div>
                <Label htmlFor="parentEmail">Parent/Guardian Email</Label>
                <Input
                  id="parentEmail"
                  type="email"
                  value={formData.parentEmail}
                  onChange={(e) => handleInputChange('parentEmail', e.target.value)}
                  placeholder="Enter parent email"
                />
              </div>
              <div>
                <Label htmlFor="parentOccupation">Occupation</Label>
                <Input
                  id="parentOccupation"
                  value={formData.parentOccupation}
                  onChange={(e) => handleInputChange('parentOccupation', e.target.value)}
                  placeholder="Enter occupation"
                />
              </div>
              <div>
                <Label htmlFor="parentWorkplace">Workplace</Label>
                <Input
                  id="parentWorkplace"
                  value={formData.parentWorkplace}
                  onChange={(e) => handleInputChange('parentWorkplace', e.target.value)}
                  placeholder="Enter workplace"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="parentAddress">Parent/Guardian Address</Label>
              <Textarea
                id="parentAddress"
                value={formData.parentAddress}
                onChange={(e) => handleInputChange('parentAddress', e.target.value)}
                placeholder="Enter parent address"
                rows={3}
              />
            </div>
          </div>
        );

      case 5: // Medical Information
        return (
          <div className="space-y-6">
            <div>
              <Label htmlFor="medicalConditions">Medical Conditions</Label>
              <Textarea
                id="medicalConditions"
                value={formData.medicalConditions}
                onChange={(e) => handleInputChange('medicalConditions', e.target.value)}
                placeholder="List any medical conditions"
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="allergies">Allergies</Label>
              <Textarea
                id="allergies"
                value={formData.allergies}
                onChange={(e) => handleInputChange('allergies', e.target.value)}
                placeholder="List any allergies"
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="medications">Current Medications</Label>
              <Textarea
                id="medications"
                value={formData.medications}
                onChange={(e) => handleInputChange('medications', e.target.value)}
                placeholder="List current medications"
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="specialNeeds">Special Needs</Label>
              <Textarea
                id="specialNeeds"
                value={formData.specialNeeds}
                onChange={(e) => handleInputChange('specialNeeds', e.target.value)}
                placeholder="Describe any special needs or accommodations required"
                rows={3}
              />
            </div>
          </div>
        );

      case 6: // Additional Information
        return (
          <div className="space-y-6">
            <div>
              <Label htmlFor="transportation">Transportation Method</Label>
              <Select value={formData.transportation} onValueChange={(value) => handleInputChange('transportation', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select transportation method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="school_bus">School Bus</SelectItem>
                  <SelectItem value="parent_drop">Parent Drop-off</SelectItem>
                  <SelectItem value="public_transport">Public Transport</SelectItem>
                  <SelectItem value="walking">Walking</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="interests">Interests & Hobbies</Label>
              <Textarea
                id="interests"
                value={formData.interests}
                onChange={(e) => handleInputChange('interests', e.target.value)}
                placeholder="Describe student's interests and hobbies"
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="goals">Educational Goals</Label>
              <Textarea
                id="goals"
                value={formData.goals}
                onChange={(e) => handleInputChange('goals', e.target.value)}
                placeholder="Describe student's educational goals"
                rows={3}
              />
            </div>
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="previousDisciplinary"
                  checked={formData.previousDisciplinary}
                  onCheckedChange={(checked) => handleInputChange('previousDisciplinary', checked)}
                />
                <Label htmlFor="previousDisciplinary">Has the student had any previous disciplinary issues?</Label>
              </div>
              {formData.previousDisciplinary && (
                <div>
                  <Label htmlFor="disciplinaryDetails">Disciplinary Details</Label>
                  <Textarea
                    id="disciplinaryDetails"
                    value={formData.disciplinaryDetails}
                    onChange={(e) => handleInputChange('disciplinaryDetails', e.target.value)}
                    placeholder="Please provide details"
                    rows={3}
                  />
                </div>
              )}
            </div>
          </div>
        );

      case 7: // Documents & Review
        return (
          <div className="space-y-6">
            {/* Document Upload */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Required Documents</h3>
              <div className="space-y-3">
                {requiredDocuments.map((doc, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <FileText className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="font-medium">{doc.name}</p>
                        {doc.required && <Badge variant="destructive" className="text-xs">Required</Badge>}
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* File Upload */}
            <div>
              <Label>Upload Documents</Label>
              <div className="mt-2">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                />
                <Button asChild variant="outline">
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <Upload className="w-4 h-4 mr-2" />
                    Choose Files
                  </label>
                </Button>
              </div>
            </div>

            {/* Uploaded Files */}
            {uploadedFiles.length > 0 && (
              <div>
                <h4 className="font-medium mb-3">Uploaded Files</h4>
                <div className="space-y-2">
                  {uploadedFiles.map((file) => (
                    <div key={file.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center space-x-3">
                        {getFileIcon(file.type)}
                        <div>
                          <p className="font-medium">{file.name}</p>
                          <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Select value={file.documentType} onValueChange={(value) => handleSetDocumentType(file.id, value)}>
                          <SelectTrigger className="w-32">
                            <SelectValue placeholder="Type" />
                          </SelectTrigger>
                          <SelectContent>
                            {requiredDocuments.map((doc) => (
                              <SelectItem key={doc.type} value={doc.type}>{doc.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button variant="outline" size="sm" onClick={() => handleRemoveFile(file.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Terms and Conditions */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Terms and Conditions</h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onCheckedChange={(checked) => handleInputChange('agreeToTerms', checked)}
                  />
                  <Label htmlFor="agreeToTerms" className="text-sm">
                    I agree to the school's terms and conditions and understand that admission is subject to approval.
                  </Label>
                </div>
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="agreeToPrivacy"
                    checked={formData.agreeToPrivacy}
                    onCheckedChange={(checked) => handleInputChange('agreeToPrivacy', checked)}
                  />
                  <Label htmlFor="agreeToPrivacy" className="text-sm">
                    I consent to the collection and processing of personal data as described in the privacy policy.
                  </Label>
                </div>
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="agreeToMedical"
                    checked={formData.agreeToMedical}
                    onCheckedChange={(checked) => handleInputChange('agreeToMedical', checked)}
                  />
                  <Label htmlFor="agreeToMedical" className="text-sm">
                    I consent to emergency medical treatment if required and understand the school's medical policies.
                  </Label>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Student Admission Application</h1>
              <p className="text-gray-600">Join Bridge of Life Academy</p>
            </div>
            <Button variant="outline" onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Step {currentStep + 1} of {steps.length}
            </span>
            <span className="text-sm text-gray-500">{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Steps Navigation */}
        <div className="mb-8">
          <div className="flex space-x-2 overflow-x-auto">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;
              const isValid = isStepValid(index);
              
              return (
                <button
                  key={step.id}
                  onClick={() => setCurrentStep(index)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive 
                      ? 'bg-blue-100 text-blue-700' 
                      : isCompleted 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{step.title}</span>
                  {isCompleted && <CheckCircle className="w-4 h-4" />}
                  {isActive && !isValid && <AlertCircle className="w-4 h-4 text-yellow-500" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              {React.createElement(steps[currentStep].icon, { className: "w-5 h-5 mr-2" })}
              {steps[currentStep].title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {renderStepContent()}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 0}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          <div className="flex space-x-2">
            {currentStep < steps.length - 1 ? (
              <Button
                onClick={handleNext}
                disabled={!isStepValid(currentStep)}
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={saving || !isStepValid(currentStep)}
              >
                {saving ? (
                  <>
                    <LoadingSpinner />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Submit Application
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdmissionForm;
